/**
 * The owner's approval calls.
 *
 * Everything that approves sends back the `rowVersion` of what was on screen.
 * If the clerk changed a row while the owner was reading it, the server refuses
 * with 409 and names the workers who moved — rather than stamping figures
 * nobody read.
 */

import { request } from './api';
import type { SectionName } from './day';

export interface OnScreenEntry {
  workerId: string;
  rowVersion: number;
}

export const approvalsApi = {
  /** The queue: the oldest day still waiting, and how many are behind it. */
  next: () =>
    request<{ workDate: string | null; moreWaiting: number; totalWaiting: number }>(
      '/approvals/next',
    ),

  approveEntry: (date: string, workerId: string, rowVersion: number) =>
    request<{ workerId: string; status: string; rowVersion: number }>(
      `/days/${date}/entries/${workerId}/approve`,
      { method: 'POST', body: { rowVersion } },
    ),

  /** The owner's own change to a man's cost code, on a row not yet approved. */
  setCostCode: (date: string, workerId: string, costCodeId: string | null, rowVersion: number) =>
    request<{ workerId: string; costCodeId: string | null; rowVersion: number }>(
      `/days/${date}/entries/${workerId}/cost-code`,
      { method: 'PUT', body: { costCodeId, rowVersion } },
    ),

  undoEntry: (date: string, workerId: string) =>
    request<{ workerId: string; status: string }>(
      `/days/${date}/entries/${workerId}/approve`,
      { method: 'DELETE' },
    ),

  /** Sends that man back to the office and unlocks labour for the clerk. */
  rejectEntry: (date: string, workerId: string, note?: string) =>
    request<{ workerId: string; status: string; note: string | null }>(
      `/days/${date}/entries/${workerId}/reject`,
      { method: 'POST', body: { note: note ?? null } },
    ),

  approveAll: (date: string, entries: OnScreenEntry[]) =>
    request<{ approved: number }>(`/days/${date}/entries/approve-all`, {
      method: 'POST',
      body: { entries },
    }),

  approveSection: (date: string, section: SectionName, entries: OnScreenEntry[]) =>
    request<{ section: SectionName; status: string; approvedAt: string }>(
      `/days/${date}/sections/${section}/approve`,
      { method: 'POST', body: { entries } },
    ),

  undoSection: (date: string, section: SectionName) =>
    request<{ section: SectionName; status: string }>(
      `/days/${date}/sections/${section}/approve`,
      { method: 'DELETE' },
    ),

  /** "Approve the day and let the clerk start tomorrow." */
  closeDay: (date: string) =>
    request<{ workDate: string; closedAt: string }>(`/days/${date}/close`, { method: 'POST' }),

  reopenDay: (date: string) =>
    request<{ workDate: string; closedAt: null }>(`/days/${date}/close`, { method: 'DELETE' }),
};
